import { ArrowDown, ArrowUp, Calendar, ChevronDown, Clock, GripVertical, MapPin, Plus, Search, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { typography } from '../../styles/typography';
import { formatTimeDisplay } from '../../utils/timeUtils';
import MapComponent from '../Dashboard/MapComponent';
import { qrApi } from '../QR/QRApi';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card, PrimaryButton } from '../ui';
import { patrollingApi } from './PatrollingApi';

const CreatePatrollingSchedule = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const patrolIdParam = searchParams.get('patrolId');
  const isEditMode = !!patrolIdParam;
  
  // Overlay context for TimePicker
  const { 
    openOverlay, 
    closeOverlay, 
    startTimeData, 
    endTimeData, 
    setStartTimeData, 
    setEndTimeData 
  } = useOverlay();
  
  // State for QR locations
  const [startQRLocation, setStartQRLocation] = useState(null);
  const [endQRLocation, setEndQRLocation] = useState(null);
  
  // State for date and time inputs
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState(''); // Stored as HH:mm (24-hour format)
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState(''); // Stored as HH:mm (24-hour format)
  
  // State for QR scanners
  const [qrScanners, setQrScanners] = useState([]);
  
  // State for drag and drop
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // State for patrol type
  const [patrolType, setPatrolType] = useState(''); // 'on-foot' or 'vehicle'
  
  // State for original patrol data (in edit mode)
  const [originalPatrolData, setOriginalPatrolData] = useState(null);
  
  // State for QR location overlay
  const [showQRLocationOverlay, setShowQRLocationOverlay] = useState(false);
  const [qrSelectionType, setQrSelectionType] = useState(null); // 'start', 'end', or 'scanner'
  
  // State for multi-select QR locations (only for scanner type)
  const [selectedQRLocationIds, setSelectedQRLocationIds] = useState(new Set());
  
  // State for QR locations from API
  const [qrLocations, setQrLocations] = useState([]);
  const [isLoadingQRLocations, setIsLoadingQRLocations] = useState(false);
  const [qrLocationsError, setQrLocationsError] = useState(null);
  
  // State for QR location search
  const [qrSearchTerm, setQrSearchTerm] = useState('');
  const [filteredQRLocations, setFilteredQRLocations] = useState([]);
  
  // State for site filter
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('all'); // 'all' or specific site_id
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Validation error states for date/time
  const [startDateError, setStartDateError] = useState('');
  const [startTimeError, setStartTimeError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [endTimeError, setEndTimeError] = useState('');

  // Refs for date pickers
  const startDatePickerRef = useRef(null);
  const endDatePickerRef = useRef(null);
  
  // Ref for map component
  const mapComponentRef = useRef(null);
  
  // State for mobile viewport detection
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 768);

  // Helper function to convert 24-hour format (HH:mm) to 12-hour format object
  const convert24HourTo12Hour = (timeString) => {
    if (!timeString) return { hours: 12, minutes: 0, period: 'AM' };
    try {
      const [hours, minutes] = timeString.split(':').map(Number);
      let hour12 = hours % 12;
      if (hour12 === 0) hour12 = 12;
      const period = hours >= 12 ? 'PM' : 'AM';
      return { hours: hour12, minutes, period };
    } catch (e) {
      return { hours: 12, minutes: 0, period: 'AM' };
    }
  };

  // Helper function to convert 12-hour format object to 24-hour format (HH:mm)
  const convert12HourTo24Hour = (timeObject) => {
    if (!timeObject) return '';
    try {
      let hours = timeObject.hours;
      if (timeObject.period === 'PM' && hours !== 12) {
        hours += 12;
      } else if (timeObject.period === 'AM' && hours === 12) {
        hours = 0;
      }
      return `${String(hours).padStart(2, '0')}:${String(timeObject.minutes).padStart(2, '0')}`;
    } catch (e) {
      return '';
    }
  };

  // Helper function to format time for display (12-hour format)
  const formatTimeForDisplay = (timeString) => {
    if (!timeString) return '';
    const timeObj = convert24HourTo12Hour(timeString);
    return formatTimeDisplay(timeObj);
  };

  // Refs to track previous context time values to prevent unnecessary updates
  const prevStartTimeDataRef = useRef(null);
  const prevEndTimeDataRef = useRef(null);

  // Sync context time data with local state when context changes
  useEffect(() => {
    if (startTimeData && startTimeData.hours !== undefined) {
      // Check if the time data actually changed to prevent infinite loops
      const timeDataString = JSON.stringify(startTimeData);
      if (timeDataString !== JSON.stringify(prevStartTimeDataRef.current)) {
        prevStartTimeDataRef.current = startTimeData;
        const time24Hour = convert12HourTo24Hour(startTimeData);
        // Only update if different from current value
        if (time24Hour && time24Hour !== startTime) {
          setStartTime(time24Hour);
        }
      }
    }
  }, [startTimeData]);

  useEffect(() => {
    if (endTimeData && endTimeData.hours !== undefined) {
      // Check if the time data actually changed to prevent infinite loops
      const timeDataString = JSON.stringify(endTimeData);
      if (timeDataString !== JSON.stringify(prevEndTimeDataRef.current)) {
        prevEndTimeDataRef.current = endTimeData;
        const time24Hour = convert12HourTo24Hour(endTimeData);
        // Only update if different from current value
        if (time24Hour && time24Hour !== endTime) {
          setEndTime(time24Hour);
        }
      }
    }
  }, [endTimeData]);

  // Load patrol data if in edit mode
  useEffect(() => {
    if (isEditMode && patrolIdParam) {
      loadPatrolDataForEdit();
    } else {
      // Clear edit data if not in edit mode
      localStorage.removeItem('editPatrolData');
    }
  }, [isEditMode, patrolIdParam]);

  // Effect to handle responsive layout on mobile screens
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      // Mobile breakpoint: < 768px
      const isMobile = width < 768;
      setIsMobileViewport(isMobile);
      setViewportWidth(width);
    };

    // Check on mount
    handleResize();

    // Listen to resize events
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Restore form data from localStorage if coming back from assign-patrol-convoy
  useEffect(() => {
    const storedFormData = localStorage.getItem('createPatrolFormData');
    if (storedFormData) {
      try {
        const formData = JSON.parse(storedFormData);
        
        // Restore form state
        if (formData.startDate) setStartDate(formData.startDate);
        if (formData.startTime) setStartTime(formData.startTime);
        if (formData.endDate) setEndDate(formData.endDate);
        if (formData.endTime) setEndTime(formData.endTime);
        if (formData.qrScanners) setQrScanners(formData.qrScanners);
        if (formData.patrolType) setPatrolType(formData.patrolType);
        
        // Restore selected QR locations (need to wait for QR locations to load)
        if (formData.startQRLocationId) {
          // Will be set after QR locations load
        }
        if (formData.endQRLocationId) {
          // Will be set after QR locations load
        }
      } catch (error) {
        console.error('Error restoring form data:', error);
      }
    }
  }, []);

  // Restore selected QR locations after they are loaded
  useEffect(() => {
    if (qrLocations.length > 0) {
      // In edit mode, load QR locations from original patrol data
      if (isEditMode && originalPatrolData) {
        console.log('🔄 Loading QR locations for edit mode...');
        
        // Set start QR location
        if (originalPatrolData.start_qr_id) {
          const startQr = qrLocations.find(q => q.id === originalPatrolData.start_qr_id);
          if (startQr) {
            console.log('✅ Found start QR:', startQr);
            setStartQRLocation(startQr);
          } else {
            console.warn('⚠️ Start QR not found in qrLocations:', originalPatrolData.start_qr_id);
          }
        }
        
        // Set end QR location
        if (originalPatrolData.end_qr_id) {
          const endQr = qrLocations.find(q => q.id === originalPatrolData.end_qr_id);
          if (endQr) {
            console.log('✅ Found end QR:', endQr);
            setEndQRLocation(endQr);
          } else {
            console.warn('⚠️ End QR not found in qrLocations:', originalPatrolData.end_qr_id);
          }
        }
        
        // Set QR sequence (scanners)
        if (originalPatrolData.qr_sequence && Array.isArray(originalPatrolData.qr_sequence)) {
          // Filter out start and end QR from sequence to get intermediate scanners
          const scannerQrs = originalPatrolData.qr_sequence.filter(
            qrId => qrId !== originalPatrolData.start_qr_id && qrId !== originalPatrolData.end_qr_id
          );
          
          const scanners = scannerQrs
            .map(qrId => qrLocations.find(q => q.id === qrId))
            .filter(Boolean); // Remove any undefined values
          
          if (scanners.length > 0) {
            console.log('✅ Found QR scanners:', scanners);
            setQrScanners(scanners);
          }
        }
      } else {
        // Not in edit mode, restore from localStorage if available
        const storedFormData = localStorage.getItem('createPatrolFormData');
        if (storedFormData) {
          try {
            const formData = JSON.parse(storedFormData);
            if (formData.startQRLocationId) {
              const qr = qrLocations.find(q => q.id === formData.startQRLocationId);
              if (qr) setStartQRLocation(qr);
            }
            if (formData.endQRLocationId) {
              const qr = qrLocations.find(q => q.id === formData.endQRLocationId);
              if (qr) setEndQRLocation(qr);
            }
          } catch (error) {
            console.error('Error restoring QR location data:', error);
          }
        }
      }
    }
  }, [qrLocations, isEditMode, originalPatrolData]);

  // Fetch QR locations from API with pagination support
  const fetchQRLocations = async (lastKey = null, limit = 100) => {
    setIsLoadingQRLocations(true);
    setQrLocationsError(null);
    
    try {
      let allLocations = [];
      let currentLastKey = lastKey;
      let hasMore = true;
      
      // If last_key is provided, fetch only one batch (pagination request)
      if (currentLastKey) {
        console.log('Fetching QR locations with pagination (last_key provided)');
        const response = await qrApi.getAll({ last_key: currentLastKey, limit });
        
        // Transform API response to component format
        const transformedLocations = response.items.map((qr) => ({
          id: qr.qr_id,
          name: qr.name || 'Unnamed QR',
          address: qr.landmark || 'No landmark specified',
          coordinates: {
            latitude: typeof qr.location?.latitude === 'string' 
              ? parseFloat(qr.location.latitude) 
              : qr.location?.latitude || 0,
            longitude: typeof qr.location?.longitude === 'string'
              ? parseFloat(qr.location.longitude)
              : qr.location?.longitude || 0,
          },
          site_id: qr.site_id,
          s3_url: qr.s3_url,
          // Keep original data for reference
          originalData: qr,
        }));
        
        allLocations = transformedLocations;
        console.log('QR locations fetched (pagination):', transformedLocations.length);
      } else {
        // No last_key provided - loop to fetch up to the limit (initial request)
        console.log('Fetching all QR locations (initial request, looping to fetch up to limit)');
        
        while (hasMore) {
          const response = await qrApi.getAll({ 
            last_key: currentLastKey, 
            limit 
          });
          
          // Transform API response to component format
          const transformedLocations = response.items.map((qr) => ({
            id: qr.qr_id,
            name: qr.name || 'Unnamed QR',
            address: qr.landmark || 'No landmark specified',
            coordinates: {
              latitude: typeof qr.location?.latitude === 'string' 
                ? parseFloat(qr.location.latitude) 
                : qr.location?.latitude || 0,
              longitude: typeof qr.location?.longitude === 'string'
                ? parseFloat(qr.location.longitude)
                : qr.location?.longitude || 0,
            },
            site_id: qr.site_id,
            s3_url: qr.s3_url,
            // Keep original data for reference
            originalData: qr,
          }));
          
          allLocations = [...allLocations, ...transformedLocations];
          
          // Update pagination state
          currentLastKey = response.last_key || response.last_evaluated_key || null;
          hasMore = response.has_more && currentLastKey !== null;
          
          console.log(`Fetched batch: ${transformedLocations.length} items. Total so far: ${allLocations.length}. Has more: ${hasMore}`);
          
          // Safety check to prevent infinite loops
          if (allLocations.length >= limit * 10) {
            console.warn('Reached maximum limit for QR locations fetch. Stopping pagination.');
            break;
          }
        }
        
        console.log('QR locations fetched successfully (all batches):', allLocations.length);
      }
      
      setQrLocations(allLocations);
    } catch (error) {
      console.error('Error fetching QR locations:', error);
      setQrLocationsError(error.message || 'Failed to fetch QR locations. Please try again.');
      setQrLocations([]);
    } finally {
      setIsLoadingQRLocations(false);
    }
  };

  // Fetch QR locations on mount (for restoration from cache)
  useEffect(() => {
    fetchQRLocations();
  }, []);

  // Fetch sites
  const fetchSites = async () => {
    setIsLoadingSites(true);
    try {
      const response = await getAllSites();
      // Handle different response formats
      const siteList = Array.isArray(response?.sites)
        ? response.sites
        : Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];
      
      // Sort sites by name
      const sorted = [...siteList].sort((a, b) =>
        (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
      );
      
      setSites(sorted);
      console.log('Sites fetched successfully:', sorted.length);
    } catch (error) {
      console.error('Error fetching sites:', error);
      setSites([]);
    } finally {
      setIsLoadingSites(false);
    }
  };

  // Fetch QR locations and sites when overlay opens
  useEffect(() => {
    if (showQRLocationOverlay && !isLoadingQRLocations) {
      // Fetch fresh data each time overlay opens, or if we don't have any data yet
      if (qrLocations.length === 0) {
        fetchQRLocations();
      }
      // Fetch sites if not already loaded
      if (sites.length === 0) {
        fetchSites();
      }
      // Reset search and site filter when overlay opens
      setQrSearchTerm('');
      setSelectedSiteId('all');
      // Clear selected QR location IDs when overlay opens
      setSelectedQRLocationIds(new Set());
    }
  }, [showQRLocationOverlay]);

  // Filter QR locations based on search term and selected site
  useEffect(() => {
    let filtered = qrLocations;

    // Filter by site
    if (selectedSiteId && selectedSiteId !== 'all') {
      filtered = filtered.filter(qr => qr.site_id === selectedSiteId);
    }

    // Filter by search term
    if (qrSearchTerm) {
      const query = qrSearchTerm.toLowerCase();
      filtered = filtered.filter(qr => {
        const name = (qr.name || '').toLowerCase();
        const address = (qr.address || '').toLowerCase();
        const id = (qr.id || '').toLowerCase();
        return name.includes(query) || address.includes(query) || id.includes(query);
      });
    }

    setFilteredQRLocations(filtered);
  }, [qrSearchTerm, qrLocations, selectedSiteId]);

  // Validate date and time combinations
  const validateDateTimeSelection = () => {
    // Clear previous errors
    setStartDateError('');
    setStartTimeError('');
    setEndDateError('');
    setEndTimeError('');

    // Validate "Start" date
    if (startDate) {
      const startDateObj = parseDate(startDate);
      if (!startDateObj) {
        setStartDateError('Please select a valid date');
      } else {
        // Check if Start Date is in the past (more than 30 minutes ago if today)
        const now = new Date();
        const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startDateMidnight = new Date(startDateObj.getFullYear(), startDateObj.getMonth(), startDateObj.getDate());

        if (startDateMidnight.getTime() < todayMidnight.getTime()) {
          setStartDateError('Cannot select a past date');
        }
      }
    }

    // Validate "Start" time
    if (startTime) {
      const startTimeObj = parseTime(startTime);
      if (!startTimeObj) {
        setStartTimeError('Please select a valid time');
      } else if (startDate) {
        const startDateObj = parseDate(startDate);
        if (startDateObj) {
          const now = new Date();
          const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const startDateMidnight = new Date(startDateObj.getFullYear(), startDateObj.getMonth(), startDateObj.getDate());

          // If Start Date is today, check if time is more than 30 minutes in the past
          if (startDateMidnight.getTime() === todayMidnight.getTime()) {
            const currentTime = { hour: now.getHours(), minute: now.getMinutes() };
            const startMinutes = startTimeObj.hour * 60 + startTimeObj.minute;
            const currentMinutes = currentTime.hour * 60 + currentTime.minute;
            const thirtyMinutesAgo = currentMinutes - 30;

            if (startMinutes < thirtyMinutesAgo) {
              setStartTimeError('Cannot select time more than 30 minutes in the past');
            }
          }
        }
      }
    }

    // Validate "End" date
    if (endDate) {
      const endDateObj = parseDate(endDate);
      if (!endDateObj) {
        setEndDateError('Please select a valid date');
      } else if (startDate) {
        const startDateObj = parseDate(startDate);
        if (startDateObj) {
          // Check if "End" date is before "Start" date
          if (endDateObj.getTime() < startDateObj.getTime()) {
            setEndDateError('End date must be after start date');
          }
        }
      }
    }

    // Validate "End" time
    if (endTime) {
      const endTimeObj = parseTime(endTime);
      if (!endTimeObj) {
        setEndTimeError('Please select a valid time');
      } else if (startDate && endDate && startTime) {
        const startDateObj = parseDate(startDate);
        const endDateObj = parseDate(endDate);
        const startTimeObj = parseTime(startTime);

        if (startDateObj && endDateObj && startTimeObj) {
          // If same date, check if times are valid
          if (endDateObj.getTime() === startDateObj.getTime()) {
            const startMinutes = startTimeObj.hour * 60 + startTimeObj.minute;
            const endMinutes = endTimeObj.hour * 60 + endTimeObj.minute;

            if (endMinutes <= startMinutes) {
              setEndTimeError('Please select a valid time. End time must be after start time');
            }
          } else if (endDateObj.getTime() < startDateObj.getTime()) {
            // If End date is before Start date, time is also invalid
            setEndTimeError('Please select a valid date first');
          }
        }
      }
    }
  };

  // Validate when date/time values change
  useEffect(() => {
    validateDateTimeSelection();
  }, [startDate, startTime, endDate, endTime]);

  // Handle QR location checkbox toggle (for multi-select)
  const handleQRLocationToggle = (qrLocationId) => {
    setSelectedQRLocationIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(qrLocationId)) {
        newSet.delete(qrLocationId);
      } else {
        newSet.add(qrLocationId);
      }
      return newSet;
    });
  };

  // Handle QR location selection (for single select - start/end)
  const handleQRLocationSelect = (qrLocation) => {
    if (!qrLocation) return;
    
    // Prepare QR location data with all necessary fields
    const selectedQRLocation = {
      id: qrLocation.id,
      qr_id: qrLocation.id, // Keep qr_id for API compatibility
      name: qrLocation.name,
      address: qrLocation.address,
      landmark: qrLocation.address, // Use address as landmark
      coordinates: qrLocation.coordinates,
      site_id: qrLocation.site_id,
      s3_url: qrLocation.s3_url,
      // Keep original data if available
      originalData: qrLocation.originalData,
    };
    
    if (qrSelectionType === 'start') {
      setStartQRLocation(selectedQRLocation);
      setShowQRLocationOverlay(false);
      setQrSelectionType(null);
    } else if (qrSelectionType === 'end') {
      setEndQRLocation(selectedQRLocation);
      setShowQRLocationOverlay(false);
      setQrSelectionType(null);
    }
    // For scanner type, we use multi-select with confirm button
  };

  // Handle confirming selected QR locations (for scanner type)
  const handleConfirmQRLocationSelection = () => {
    if (qrSelectionType !== 'scanner' || selectedQRLocationIds.size === 0) {
      return;
    }

    // Get selected QR locations from filtered list
    const selectedQRLocations = filteredQRLocations
      .filter(qr => selectedQRLocationIds.has(qr.id))
      .map(qrLocation => ({
        id: qrLocation.id,
        qr_id: qrLocation.id,
        name: qrLocation.name,
        address: qrLocation.address,
        landmark: qrLocation.address,
        coordinates: qrLocation.coordinates,
        site_id: qrLocation.site_id,
        s3_url: qrLocation.s3_url,
        originalData: qrLocation.originalData,
      }));

    // Add all selected QR locations to scanners
    setQrScanners(prev => [...prev, ...selectedQRLocations]);
    
    // Close overlay and reset
    setShowQRLocationOverlay(false);
    setQrSelectionType(null);
    setSelectedQRLocationIds(new Set());
  };

  const handleRemoveQRScanner = (index) => {
    setQrScanners(prev => prev.filter((_, i) => i !== index));
  };

  // Handle moving QR scanner up in sequence
  const handleMoveQRScannerUp = (index) => {
    if (index === 0) return; // Already at the top
    setQrScanners(prev => {
      const newScanners = [...prev];
      [newScanners[index - 1], newScanners[index]] = [newScanners[index], newScanners[index - 1]];
      return newScanners;
    });
  };

  // Handle moving QR scanner down in sequence
  const handleMoveQRScannerDown = (index) => {
    setQrScanners(prev => {
      if (index === prev.length - 1) return prev; // Already at the bottom
      const newScanners = [...prev];
      [newScanners[index], newScanners[index + 1]] = [newScanners[index + 1], newScanners[index]];
      return newScanners;
    });
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    // Only start drag if clicking on the card itself, not on buttons
    if (e.target.closest('button')) {
      e.preventDefault();
      return;
    }
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Add visual feedback
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e) => {
    // Reset visual feedback
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '';
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    // Only clear if we're actually leaving the element (not just moving to a child)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    setQrScanners(prev => {
      const newScanners = [...prev];
      const draggedItem = newScanners[draggedIndex];
      
      // Remove the dragged item
      newScanners.splice(draggedIndex, 1);
      
      // Calculate the correct drop index after removal
      const adjustedDropIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
      
      // Insert at the new position
      newScanners.splice(adjustedDropIndex, 0, draggedItem);
      
      return newScanners;
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Handle opening TimePicker overlays
  const handleOpenStartTimePicker = () => {
    // Set initial time in context if we have a start time
    if (startTime) {
      const time12Hour = convert24HourTo12Hour(startTime);
      setStartTimeData(time12Hour);
    }
    // Set end time in context for validation
    if (endTime) {
      const time12Hour = convert24HourTo12Hour(endTime);
      setEndTimeData(time12Hour);
    }
    openOverlay('startTimePicker');
  };

  const handleOpenEndTimePicker = () => {
    // Set initial time in context if we have an end time
    if (endTime) {
      const time12Hour = convert24HourTo12Hour(endTime);
      setEndTimeData(time12Hour);
    }
    // Set start time in context for validation
    if (startTime) {
      const time12Hour = convert24HourTo12Hour(startTime);
      setStartTimeData(time12Hour);
    }
    openOverlay('endTimePicker');
  };

  // Load patrol data for edit mode
  const loadPatrolDataForEdit = async () => {
    if (!patrolIdParam) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Loading patrol data for edit mode:', patrolIdParam);
      
      // Try to get from localStorage first (passed from PatroleDetails)
      let patrol = null;
      const storedPatrolData = localStorage.getItem('editPatrolData');
      if (storedPatrolData) {
        try {
          patrol = JSON.parse(storedPatrolData);
          console.log('✅ Loaded patrol data from localStorage:', patrol);
        } catch (e) {
          console.error('Error parsing stored patrol data:', e);
        }
      }
      
      // If not in localStorage, fetch from API
      if (!patrol) {
        console.log('🔄 Fetching patrol data from API...');
        patrol = await patrollingApi.getById(patrolIdParam);
        console.log('✅ Fetched patrol data from API:', patrol);
      }
      
      if (!patrol) {
        throw new Error('Failed to load patrol data');
      }
      
      // Check if patrol can be edited (only scheduled patrols)
      if (patrol.status && patrol.status !== 'scheduled') {
        setError(`Cannot edit patrol with status "${patrol.status}". Only "scheduled" patrols can be edited.`);
        setIsLoading(false);
        return;
      }
      
      // Store original patrol data for reference
      setOriginalPatrolData(patrol);
      
      // Parse and set start date/time
      if (patrol.patrol_start_timestamp) {
        const startDateTime = new Date(patrol.patrol_start_timestamp);
        const startDateStr = startDateTime.toISOString().split('T')[0]; // YYYY-MM-DD
        const startHours = startDateTime.getHours();
        const startMinutes = startDateTime.getMinutes();
        const startTimeStr = `${String(startHours).padStart(2, '0')}:${String(startMinutes).padStart(2, '0')}`; // HH:mm
        
        setStartDate(startDateStr);
        setStartTime(startTimeStr);
        
        // Set context time data for TimePicker
        const startTime12Hour = convert24HourTo12Hour(startTimeStr);
        setStartTimeData(startTime12Hour);
      }
      
      // Parse and set end date/time
      if (patrol.patrol_end_timestamp) {
        const endDateTime = new Date(patrol.patrol_end_timestamp);
        const endDateStr = endDateTime.toISOString().split('T')[0]; // YYYY-MM-DD
        const endHours = endDateTime.getHours();
        const endMinutes = endDateTime.getMinutes();
        const endTimeStr = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`; // HH:mm
        
        setEndDate(endDateStr);
        setEndTime(endTimeStr);
        
        // Set context time data for TimePicker
        const endTime12Hour = convert24HourTo12Hour(endTimeStr);
        setEndTimeData(endTime12Hour);
      }
      
      // Set patrol type
      if (patrol.patrol_type) {
        setPatrolType(patrol.patrol_type === 'on_foot' ? 'on-foot' : 'vehicle');
      }
      
      // Wait for QR locations to be loaded before setting start/end QR
      // This will be handled by the useEffect that watches qrLocations
      
    } catch (error) {
      console.error('❌ Error loading patrol data for edit:', error);
      setError(`Failed to load patrol data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };


  // Convert YYYY-MM-DD to DD-MM-YYYY for display
  const formatDateForInputDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const parts = dateString.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        // YYYY-MM-DD format
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return dateString;
    } catch (e) {
      return dateString;
    }
  };

  // Format date to DD-MM-YYYY
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Convert 24-hour time (HH:mm) to 12-hour format (hh:mm a)
  const formatTimeTo12Hour = (timeString) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const minute = parseInt(minutes, 10);
      
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      
      return `${String(displayHour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`;
    } catch (e) {
      console.error('Error formatting time:', e);
      return timeString;
    }
  };

  // Parse date from YYYY-MM-DD (ISO format from date input) or DD-MM-YYYY
  const parseDate = (dateString) => {
    if (!dateString) return null;
    try {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        // Check if it's YYYY-MM-DD format (ISO format from date input)
        if (parts[0].length === 4) {
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const day = parseInt(parts[2], 10);
          return new Date(year, month, day);
        } else {
          // DD-MM-YYYY format
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          return new Date(year, month, day);
        }
      }
    } catch (e) {
      console.error('Error parsing date:', e);
    }
    return null;
  };

  // Parse time from HH:mm format (24-hour) or hh:mm a format (12-hour)
  const parseTime = (timeString) => {
    if (!timeString) return null;
    try {
      // Check if it's 12-hour format (has space and AM/PM)
      if (timeString.includes(' ')) {
        const timeParts = timeString.split(' ');
        if (timeParts.length === 2) {
          const time = timeParts[0].split(':');
          let hour = parseInt(time[0], 10);
          const minute = parseInt(time[1], 10);
          const period = timeParts[1].toLowerCase();

          if (period === 'pm' && hour !== 12) {
            hour += 12;
          } else if (period === 'am' && hour === 12) {
            hour = 0;
          }

          return { hour, minute };
        }
      } else {
        // 24-hour format (HH:mm)
        const time = timeString.split(':');
        const hour = parseInt(time[0], 10);
        const minute = parseInt(time[1], 10);
        return { hour, minute };
      }
    } catch (e) {
      console.error('Error parsing time:', e);
    }
    return null;
  };

  // Validate date and time constraints
  const validateDateTimeConstraints = () => {
    const startDateObj = parseDate(startDate);
    const endDateObj = parseDate(endDate);
    const startTimeObj = parseTime(startTime);
    const endTimeObj = parseTime(endTime);

    if (!startDateObj || !endDateObj || !startTimeObj || !endTimeObj) {
      alert('Please select valid dates and times');
      return false;
    }

    // Check if "End Date" is before "Start Date"
    if (endDateObj < startDateObj) {
      alert('End date must be after start date');
      return false;
    }

    // If same date, check if times are valid
    if (endDateObj.getTime() === startDateObj.getTime()) {
      const startMinutes = startTimeObj.hour * 60 + startTimeObj.minute;
      const endMinutes = endTimeObj.hour * 60 + endTimeObj.minute;

      if (endMinutes <= startMinutes) {
        alert('From and to time cannot be same');
        return false;
      }
    }

    // Check if Start Date is today and time is more than 30 minutes in the past
    const now = new Date();
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startDateMidnight = new Date(startDateObj.getFullYear(), startDateObj.getMonth(), startDateObj.getDate());

    if (startDateMidnight.getTime() === todayMidnight.getTime()) {
      const currentTime = { hour: now.getHours(), minute: now.getMinutes() };
      const startMinutes = startTimeObj.hour * 60 + startTimeObj.minute;
      const currentMinutes = currentTime.hour * 60 + currentTime.minute;
      const thirtyMinutesAgo = currentMinutes - 30;

      if (startMinutes < thirtyMinutesAgo) {
        alert('Cannot select time more than 30 minutes in the past');
        return false;
      }
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = () => {
    // Check for validation errors first
    if (startDateError || startTimeError || endDateError || endTimeError) {
      alert('Please fix the date and time errors before submitting');
      return;
    }

    // Validate required fields
    if (!startQRLocation || !endQRLocation || !startDate || !startTime || !endDate || !endTime) {
      alert('Please fill all the above details to continue');
      return;
    }

    // Validate date and time constraints
    if (!validateDateTimeConstraints()) {
      return;
    }

    // Prepare patrol data
    const patrolData = {
      start_qr: startQRLocation,
      end_qr: endQRLocation,
      start_date: formatDateForDisplay(startDate),
      start_time: formatTimeTo12Hour(startTime),
      end_date: formatDateForDisplay(endDate),
      end_time: formatTimeTo12Hour(endTime),
      qr_scanners: qrScanners,
      patrol_type: patrolType,
      site_id: startQRLocation?.site_id, // Include site_id from start QR location
    };

    // In edit mode, include original patrol data for reference
    if (isEditMode && originalPatrolData) {
      patrolData.originalPatrol = originalPatrolData;
      patrolData.patrol_id = patrolIdParam;
    }

    console.log('Patrol Schedule Data:', patrolData);
    
    // Store patrol data in localStorage as backup (for assign-patrol-convoy)
    localStorage.setItem('pendingPatrolData', JSON.stringify(patrolData));
    
    // Store form data in localStorage for restoration if user comes back
    const formData = {
      startDate,
      startTime,
      endDate,
      endTime,
      qrScanners,
      patrolType,
      startQRLocationId: startQRLocation?.id,
      endQRLocationId: endQRLocation?.id,
    };
    localStorage.setItem('createPatrolFormData', JSON.stringify(formData));
    
    // Navigate to patrol officer assignment page
    const encodedPatrolData = encodeURIComponent(JSON.stringify(patrolData));
    if (isEditMode) {
      setSearchParams({ view: 'assign-patrol-convoy', patrolData: encodedPatrolData, patrolId: patrolIdParam });
    } else {
      setSearchParams({ view: 'assign-patrol-convoy', patrolData: encodedPatrolData });
    }
  };

  // Get minimum date for date inputs (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get minimum time for time inputs
  const getMinTime = (isStartTime) => {
    if (isStartTime) {
      // For "Start" time, check if date is today
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const selectedDate = parseDate(startDate);
      
      if (selectedDate && selectedDate.getTime() === today.getTime()) {
        const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);
        return `${String(thirtyMinutesAgo.getHours()).padStart(2, '0')}:${String(thirtyMinutesAgo.getMinutes()).padStart(2, '0')}`;
      }
      return '00:00';
    } else {
      // For "End" time, check if same date as "Start" date
      const startDateObj = parseDate(startDate);
      const endDateObj = parseDate(endDate);
      
      if (startDateObj && endDateObj && startDateObj.getTime() === endDateObj.getTime() && startTime) {
        // Same date - "End" time must be after "Start" time
        const startTimeObj = parseTime(startTime);
        if (startTimeObj) {
          // Add 1 minute to start time as minimum
          const minMinutes = startTimeObj.hour * 60 + startTimeObj.minute + 1;
          const minHour = Math.floor(minMinutes / 60) % 24;
          const minMinute = minMinutes % 60;
          return `${String(minHour).padStart(2, '0')}:${String(minMinute).padStart(2, '0')}`;
        }
      }
      return '00:00';
    }
  };

  // Build patrol QR locations array for map display
  const patrolQRLocations = useMemo(() => {
    const locations = [];
    
    // Add start QR location
    if (startQRLocation && startQRLocation.coordinates) {
      locations.push({
        qr_code: startQRLocation.id || startQRLocation.qr_id,
        location_name: startQRLocation.name || 'Start Location',
        latitude: startQRLocation.coordinates.latitude,
        longitude: startQRLocation.coordinates.longitude,
        type: 'start',
        checkpoint_number: null,
        scanned: false,
        scan_timestamp: null,
      });
    }
    
    // Add QR scanners (checkpoints)
    qrScanners.forEach((qr, index) => {
      if (qr && qr.coordinates) {
        locations.push({
          qr_code: qr.id || qr.qr_id,
          location_name: qr.name || `QR ${index + 1}`,
          latitude: qr.coordinates.latitude,
          longitude: qr.coordinates.longitude,
          type: 'checkpoint',
          checkpoint_number: index + 1,
          scanned: false,
          scan_timestamp: null,
        });
      }
    });
    
    // Add end QR location
    if (endQRLocation && endQRLocation.coordinates) {
      locations.push({
        qr_code: endQRLocation.id || endQRLocation.qr_id,
        location_name: endQRLocation.name || 'End Location',
        latitude: endQRLocation.coordinates.latitude,
        longitude: endQRLocation.coordinates.longitude,
        type: 'end',
        checkpoint_number: null,
        scanned: false,
        scan_timestamp: null,
      });
    }
    
    return locations;
  }, [startQRLocation, qrScanners, endQRLocation]);

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header Section */}
      <div className="mb-6 flex items-center gap-4">
        {/* <button
          onClick={() => setSearchParams({})}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          title="Back"
        >
          <ArrowLeft className="w-6 h-6 text-gray-600" />
        </button> */}
        <div>
          <h2 className={typography.h2} style={{ color: '#000000' }}>
            {isEditMode ? 'Edit Patrolling Schedule' : 'Create Patrolling Schedule'}
          </h2>
          <p className={typography.p} style={{ color: '#666666', marginTop: '0.5rem' }}>
            {isEditMode 
              ? 'Update the details for this patrolling schedule'
              : 'Fill in the details to create a new patrolling schedule'}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto mb-6">
        <div className="space-y-6">
          {/* Shift Details Section */}
          <div>
            <h3 className={typography.h3} style={{ color: '#000000', marginBottom: '1rem' }}>
              Shift details
            </h3>
            
            {/* Patrol Type Card */}
            <Card.Gradient className="p-6 mb-4">
              <h5 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                Patrol Type
              </h5>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setPatrolType('on-foot')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    patrolType === 'on-foot'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <svg 
                      className={`w-8 h-8 ${patrolType === 'on-foot' ? 'text-green-600' : 'text-gray-600'}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className={`font-medium ${patrolType === 'on-foot' ? 'text-green-600' : 'text-gray-700'}`}>
                      On Foot
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => setPatrolType('vehicle')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    patrolType === 'vehicle'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <svg 
                      className={`w-8 h-8 ${patrolType === 'vehicle' ? 'text-green-600' : 'text-gray-600'}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6 0a1 1 0 001 1h2a1 1 0 001-1m0 0h2a2 2 0 002-2v-3a2 2 0 00-2-2h-2.343M11 7h3" />
                    </svg>
                    <span className={`font-medium ${patrolType === 'vehicle' ? 'text-green-600' : 'text-gray-700'}`}>
                      Vehicle
                    </span>
                  </div>
                </button>
              </div>
            </Card.Gradient>
            
            {/* Start and End QR Cards in Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Start (qr) Card */}
              <Card.Gradient className="p-6">
                <h5 className={typography.h5} style={{ color: '#000000', marginBottom: '0.5rem' }}>
                  Start (qr)
                </h5>
                
                {/* Location Selection */}
                <div className="mb-4">
                  <button
                    onClick={() => {
                      setQrSelectionType('start');
                      setShowQRLocationOverlay(true);
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-3 bg-white flex items-center gap-3 hover:bg-gray-50 transition-colors"
                  >
                    <MapPin className={`w-5 h-5 ${startQRLocation ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className={`flex-1 text-left ${startQRLocation ? 'text-black' : 'text-gray-500'}`}>
                      {startQRLocation ? (startQRLocation.name || startQRLocation.address) : 'Select Start location'}
                    </span>
                  </button>
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none z-10" />
                      <input
                        type="date"
                        ref={startDatePickerRef}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        min={getMinDate()}
                        className="absolute opacity-0 w-full h-12 cursor-pointer z-20"
                        style={{ left: 0, top: 0 }}
                      />
                      <input
                        type="text"
                        value={formatDateForInputDisplay(startDate)}
                        placeholder="DD-MM-YYYY"
                        readOnly
                        onClick={() => startDatePickerRef.current?.showPicker?.() || startDatePickerRef.current?.click()}
                        className={`w-full h-12 pl-10 pr-12 border rounded-lg bg-white focus:outline-none focus:ring-2 cursor-pointer ${
                          startDateError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => startDatePickerRef.current?.showPicker?.() || startDatePickerRef.current?.click()}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors z-30"
                        title="Open date picker"
                      >
                        <Calendar className="w-5 h-5 text-gray-600" />
                      </button>
                    </div>
                    {startDateError && (
                      <p className="text-red-600 text-xs mt-1">{startDateError}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Time
                    </label>
                    <button
                      type="button"
                      onClick={handleOpenStartTimePicker}
                      className={`w-full h-12 pl-10 pr-3 border rounded-lg bg-white flex items-center gap-3 hover:bg-gray-50 transition-colors text-left ${
                        startTimeError ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      <span className={startTime ? 'text-black' : 'text-gray-500'}>
                        {startTime ? formatTimeForDisplay(startTime) : 'Select time'}
                      </span>
                    </button>
                    {startTimeError && (
                      <p className="text-red-600 text-xs mt-1">{startTimeError}</p>
                    )}
                  </div>
                </div>
              </Card.Gradient>

              {/* End (qr) Card */}
              <Card.Gradient className="p-6">
              <h5 className={typography.h5} style={{ color: '#000000', marginBottom: '0.5rem' }}>
                end (qr)
              </h5>
              
              {/* Location Selection */}
              <div className="mb-4">
                <button
                  onClick={() => {
                    setQrSelectionType('end');
                    setShowQRLocationOverlay(true);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-3 bg-white flex items-center gap-3 hover:bg-gray-50 transition-colors"
                >
                  <MapPin className={`w-5 h-5 ${endQRLocation ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className={`flex-1 text-left ${endQRLocation ? 'text-black' : 'text-gray-500'}`}>
                    {endQRLocation ? (endQRLocation.name || endQRLocation.address) : 'Select End qr'}
                  </span>
                </button>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none z-10" />
                    <input
                      type="date"
                      ref={endDatePickerRef}
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate || getMinDate()}
                      className="absolute opacity-0 w-full h-12 cursor-pointer z-20"
                      style={{ left: 0, top: 0 }}
                    />
                    <input
                      type="text"
                      value={formatDateForInputDisplay(endDate)}
                      placeholder="DD-MM-YYYY"
                      readOnly
                      onClick={() => endDatePickerRef.current?.showPicker?.() || endDatePickerRef.current?.click()}
                      className={`w-full h-12 pl-10 pr-12 border rounded-lg bg-white focus:outline-none focus:ring-2 cursor-pointer ${
                        endDateError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => endDatePickerRef.current?.showPicker?.() || endDatePickerRef.current?.click()}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors z-30"
                      title="Open date picker"
                    >
                      <Calendar className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                  {endDateError && (
                    <p className="text-red-600 text-xs mt-1">{endDateError}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time
                  </label>
                    <button
                      type="button"
                      onClick={handleOpenEndTimePicker}
                      className={`w-full h-12 pl-10 pr-3 border rounded-lg bg-white flex items-center gap-3 hover:bg-gray-50 transition-colors text-left ${
                        endTimeError ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                    <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <span className={endTime ? 'text-black' : 'text-gray-500'}>
                      {endTime ? formatTimeForDisplay(endTime) : 'Select time'}
                    </span>
                  </button>
                  {endTimeError && (
                    <p className="text-red-600 text-xs mt-1">{endTimeError}</p>
                  )}
                </div>
              </div>
              </Card.Gradient>
            </div>
          </div>

          {/* QR Scanner Section with Preview */}
          <div className="mt-12">
            <h3 className={typography.h3} style={{ color: '#000000', marginBottom: '1rem' }}>
              QR scanner
            </h3>
            
            {/* Row Layout: Two Columns - QR Scanner Section and Preview */}
            <div className={`flex flex-col md:flex-row gap-6 ${isMobileViewport ? 'min-h-0' : ''}`}>
              {/* Column 1: QR Scanner Section */}
              <div className={`flex-1 flex flex-col ${isMobileViewport ? '' : 'min-h-0'}`}>
                <Card.Gradient className="p-4 h-full flex flex-col min-h-0">
                  
                <h5 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                Add QR checkpoints in sequence
                  </h5>
                  
                  {/* Display existing QR scanners */}
                  <div className="flex-1 space-y-4 max-h-[600px] overflow-y-auto custom-scrollbar">
                    {qrScanners.map((qr, index) => (
                      <div
                        key={index}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        className={`transition-all ${
                          draggedIndex === index
                            ? 'opacity-50 scale-95'
                            : dragOverIndex === index
                            ? 'scale-105'
                            : ''
                        }`}
                        style={{ cursor: 'move' }}
                      >
                        <Card.Gradient
                          className={`p-4 ${
                            dragOverIndex === index
                              ? 'border-2 border-green-500 shadow-lg'
                              : 'border-2 border-transparent'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-2">
                              <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0 cursor-grab active:cursor-grabbing" />
                              <h5 className={typography.h5} style={{ color: '#000000' }}>
                                QR • {index + 1}
                              </h5>
                              {/* Reordering Controls */}
                              <div className="flex flex-col gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMoveQRScannerUp(index);
                                  }}
                                  disabled={index === 0}
                                  className={`p-1 rounded transition-colors ${
                                    index === 0
                                      ? 'opacity-30 cursor-not-allowed'
                                      : 'hover:bg-gray-100 cursor-pointer'
                                  }`}
                                  title="Move up"
                                >
                                  <ArrowUp className="w-4 h-4 text-gray-600" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMoveQRScannerDown(index);
                                  }}
                                  disabled={index === qrScanners.length - 1}
                                  className={`p-1 rounded transition-colors ${
                                    index === qrScanners.length - 1
                                      ? 'opacity-30 cursor-not-allowed'
                                      : 'hover:bg-gray-100 cursor-pointer'
                                  }`}
                                  title="Move down"
                                >
                                  <ArrowDown className="w-4 h-4 text-gray-600" />
                                </button>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveQRScanner(index);
                              }}
                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                              title="Remove"
                            >
                              <X className="w-5 h-5 text-gray-600" />
                            </button>
                          </div>
                          <div className="flex items-start gap-3">
                            <MapPin className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              {qr.name && (
                                <h6 className="font-semibold text-gray-900 mb-1">{qr.name}</h6>
                              )}
                              <p className="text-gray-800">{qr.address || 'Unknown Location'}</p>
                              {qr.coordinates && (
                                <p className="text-sm text-gray-500 mt-1">
                                  📍 {qr.coordinates.latitude.toFixed(6)}, {qr.coordinates.longitude.toFixed(6)}
                                </p>
                              )}
                            </div>
                          </div>
                        </Card.Gradient>
                      </div>
                    ))}
                    
                    {qrScanners.length === 0 && (
                      <div className="p-8 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg text-center">
                        <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">No QR scanners added yet</p>
                      </div>
                    )}
                  </div>

                  {/* Add QR Scanner Button */}
                  <button
                    onClick={() => {
                      setQrSelectionType('scanner');
                      setShowQRLocationOverlay(true);
                    }}
                    className="w-full h-12 border-2 border-dashed border-gray-300 rounded-lg bg-white flex items-center justify-center hover:bg-gray-50 transition-colors mt-4"
                  >
                    <Plus className="w-6 h-6 text-gray-400" />
                  </button>
                </Card.Gradient>
              </div>

              {/* Column 2: Preview Section with Google Map */}
              <div className={`flex-1 flex flex-col ${isMobileViewport ? '' : 'min-h-0'}`}>
                <Card.Gradient 
                  className={`p-4 flex flex-col ${isMobileViewport ? '' : 'h-full min-h-0'}`}
                  style={isMobileViewport ? {
                    minHeight: `${viewportWidth + 100}px` // Add extra space for padding and title
                  } : {}}
                >
                  <h5 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                    Preview
                  </h5>
                  
                  
                  {/* Map Container */}
                  <div 
                    className={`rounded-lg overflow-hidden border border-gray-200 ${isMobileViewport ? '' : 'flex-1'}`}
                    style={isMobileViewport ? {
                      height: `${viewportWidth}px`, // Height equals viewport width on mobile
                      width: '100%',
                      minHeight: '400px',
                      flexShrink: 0, // Prevent shrinking on mobile
                      position: 'relative' // Required for Google Maps to render
                    } : {
                      height: '100%',
                      minHeight: '500px',
                      width: '100%',
                      position: 'relative' // Required for Google Maps to render
                    }}
                  >
                    {patrolQRLocations.length > 0 ? (
                      <MapComponent
                        ref={mapComponentRef}
                        officeLocations={[]}
                        guardLocations={[]}
                        patrolQRLocations={patrolQRLocations}
                        allPatrolRoutes={[]}
                        selectedPatrolId={null}
                        patrolEmployees={[]}
                        onMarkerClick={() => {}}
                        mapCenter={{ lat: 20.5937, lng: 78.9629 }}
                        mapZoom={12}
                        activeRoutes={[]}
                        showDrawingTools={false}
                      />
                    ) : (
                      <div 
                        className={`w-full flex items-center justify-center bg-gray-50 border-2 border-dashed border-gray-300 ${isMobileViewport ? '' : 'h-full'}`}
                        style={isMobileViewport ? {
                          height: `${viewportWidth}px`,
                          minHeight: '400px',
                          flexShrink: 0 // Prevent shrinking on mobile
                        } : {
                          minHeight: '500px',
                          height: '100%'
                        }}
                      >
                        <div className="text-center">
                          <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-sm text-gray-500">Select QR locations to see preview on map</p>
                        </div>
                      </div>
                    )}
                  </div>
                </Card.Gradient>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Submit Button */}
      <PrimaryButton
        onClick={handleSubmit}
        className="w-full"
      >
        Proceed to convo
      </PrimaryButton>

      {/* QR Location Overlay */}
      {showQRLocationOverlay && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
              <div>
                <h3 className={typography.h3} style={{ color: '#000000' }}>
                  {qrSelectionType === 'start' 
                    ? 'Select Start QR Location'
                    : qrSelectionType === 'end'
                    ? 'Select End QR Location'
                    : 'Select QR Locations'}
                </h3>
                {qrSelectionType === 'scanner' && selectedQRLocationIds.size > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedQRLocationIds.size} location{selectedQRLocationIds.size !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchQRLocations}
                  disabled={isLoadingQRLocations}
                  className="p-2 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Refresh QR locations"
                >
                  <svg 
                    className={`w-5 h-5 text-gray-500 ${isLoadingQRLocations ? 'animate-spin' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    setShowQRLocationOverlay(false);
                    setQrSelectionType(null);
                    setSelectedQRLocationIds(new Set());
                  }}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Search Bar and Site Filter */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex gap-3">
                {/* Search Bar */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={qrSearchTerm}
                    onChange={(e) => setQrSearchTerm(e.target.value)}
                    placeholder="Search by name, address, or ID..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  {qrSearchTerm && (
                    <button
                      onClick={() => setQrSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                      title="Clear search"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  )}
                </div>
                
                {/* Site Filter Dropdown */}
                <div className="relative min-w-[200px]">
                  <select
                    value={selectedSiteId}
                    onChange={(e) => setSelectedSiteId(e.target.value)}
                    className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none cursor-pointer"
                    disabled={isLoadingSites}
                  >
                    <option value="all">All Sites</option>
                    {sites.map((site) => (
                      <option key={site.site_id} value={site.site_id}>
                        {site.site_name || site.name || 'Unnamed Site'}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* QR Locations List */}
            <div className="flex-1 overflow-y-auto p-6">
              {isLoadingQRLocations ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-4"></div>
                  <p className="text-gray-600">Loading QR locations...</p>
                </div>
              ) : qrLocationsError ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
                    <p className="font-semibold mb-1">Error loading QR locations</p>
                    <p className="text-sm">{qrLocationsError}</p>
                  </div>
                  <button
                    onClick={fetchQRLocations}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              ) : filteredQRLocations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <MapPin className="w-12 h-12 text-gray-400 mb-4" />
                  <p className="text-gray-600">
                    {qrSearchTerm ? 'No QR locations match your search' : 'No QR locations found'}
                  </p>
                  {!qrSearchTerm && (
                    <button
                      onClick={fetchQRLocations}
                      className="mt-4 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                      Refresh
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredQRLocations.map((qrLocation) => {
                    const isSelected = selectedQRLocationIds.has(qrLocation.id);
                    const isScannerType = qrSelectionType === 'scanner';
                    
                    return (
                      <div
                        key={qrLocation.id}
                        onClick={() => {
                          if (isScannerType) {
                            handleQRLocationToggle(qrLocation.id);
                          } else {
                            handleQRLocationSelect(qrLocation);
                          }
                        }}
                        className={`w-full p-4 border rounded-lg transition-colors text-left cursor-pointer ${
                          isSelected && isScannerType
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-green-500 hover:bg-green-50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {isScannerType ? (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleQRLocationToggle(qrLocation.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-1 w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-pointer"
                            />
                          ) : (
                            <MapPin className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <h5 className="font-semibold text-gray-900 mb-1">
                              {qrLocation.name}
                            </h5>
                            <p className="text-sm text-gray-600">
                              {qrLocation.address}
                            </p>
                            {qrLocation.coordinates && (
                              <p className="text-xs text-gray-500 mt-1">
                                📍 {qrLocation.coordinates.latitude.toFixed(6)}, {qrLocation.coordinates.longitude.toFixed(6)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer with Confirm Button (only for scanner type) */}
            {qrSelectionType === 'scanner' && (
              <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
                <p className="text-sm text-gray-600">
                  {selectedQRLocationIds.size > 0
                    ? `${selectedQRLocationIds.size} location${selectedQRLocationIds.size !== 1 ? 's' : ''} selected`
                    : 'Select QR locations to add as checkpoints'}
                </p>
                <PrimaryButton
                  onClick={handleConfirmQRLocationSelection}
                  disabled={selectedQRLocationIds.size === 0}
                  className="min-w-[120px]"
                >
                  Confirm ({selectedQRLocationIds.size})
                </PrimaryButton>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CreatePatrollingSchedule;

